import React, { useState, useEffect, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { REVIEWS } from '../mock';
import Reveal from './Reveal';

export default function Reviews() {
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setPerView(1);
      else if (window.innerWidth < 1024) setPerView(2);
      else setPerView(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxIndex = Math.max(0, REVIEWS.length - perView);

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));

  useEffect(() => {
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next]);

  useEffect(() => { if (index > maxIndex) setIndex(maxIndex); }, [maxIndex, index]);

  return (
    <section id="reviews" className="py-20 lg:py-28 bg-[#3D5142] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-2xl" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-[13px] tracking-[0.3em] uppercase text-[#D9A79C] font-medium">Kind Words</span>
              <h2 className="font-serif-display text-[36px] sm:text-[46px] text-white mt-3 leading-tight">Loved by our community</h2>
              <div className="flex items-center gap-2 mt-4">
                <div className="flex">{[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-[#E0A93B] text-[#E0A93B]" />)}</div>
                <span className="text-[#E9E2D6] text-[14px]">Rated 5 stars across 200+ reviews</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={prev} aria-label="Previous" className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#3D5142] transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={next} aria-label="Next" className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-[#3D5142] transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
          >
            {REVIEWS.map((r, i) => (
              <div key={i} className="shrink-0 px-3" style={{ width: `${100 / perView}%` }}>
                <div className="bg-[#FBF8F4] rounded-2xl p-7 h-full flex flex-col">
                  <Quote className="w-8 h-8 text-[#C4715A] mb-4" />
                  <div className="flex mb-3">{Array.from({ length: r.rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#E0A93B] text-[#E0A93B]" />)}</div>
                  <p className="text-[15px] leading-relaxed text-[#4A4A45] flex-1">“{r.text}”</p>
                  <div className="mt-5 pt-4 border-t border-[#EAE2D6] flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#3D5142] text-white flex items-center justify-center text-[14px] font-medium">{r.name.charAt(0)}</span>
                    <span className="font-medium text-[14px] text-[#2A2A28]">{r.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
